import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { MessageSquare, RefreshCw, Send, AlertCircle, ChevronRight, Trash2, Clock, Settings } from 'lucide-react';
import { Switch } from '../../components/ui/switch';
import { toast } from '../../hooks/use-toast';
import AdminLayout from '../../components/admin/AdminLayout';

const STATE_LABELS = {
  idle: 'Idle',
  menu: 'Menu',
  ride_pickup: 'Ride · Pickup',
  ride_dropoff: 'Ride · Dropoff',
  ride_confirm: 'Ride · Confirm',
  pkg_pickup: 'Package · Pickup',
  pkg_dropoff: 'Package · Dropoff',
  pkg_receiver_name: 'Package · Receiver',
  pkg_receiver_phone: 'Package · Phone',
  pkg_confirm: 'Package · Confirm',
  bus_origin: 'Bus · Origin',
  bus_destination: 'Bus · Destination',
  bus_date: 'Bus · Date',
  bus_select_trip: 'Bus · Select Trip',
  bus_passenger_name: 'Bus · Passenger',
  bus_confirm: 'Bus · Confirm',
  cargo_pickup: 'Cargo · Pickup',
  cargo_dropoff: 'Cargo · Dropoff',
  cargo_weight: 'Cargo · Weight',
  cargo_description: 'Cargo · Description',
  cargo_receiver_name: 'Cargo · Receiver',
  cargo_receiver_phone: 'Cargo · Phone',
  cargo_vehicle: 'Cargo · Vehicle',
  cargo_confirm: 'Cargo · Confirm',
};

const stateColor = (state) => {
  if (!state || state === 'idle') return 'bg-gray-100 text-gray-600';
  if (state.startsWith('ride')) return 'bg-blue-100 text-blue-700';
  if (state.startsWith('pkg')) return 'bg-amber-100 text-amber-700';
  if (state.startsWith('bus')) return 'bg-emerald-100 text-emerald-700';
  if (state.startsWith('cargo')) return 'bg-orange-100 text-orange-700';
  return 'bg-pink-100 text-pink-700';
};

const AdminWhatsApp = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [selected, setSelected] = useState(null);
  const [transcript, setTranscript] = useState({ session: null, messages: [] });
  const [showTest, setShowTest] = useState(false);
  const [testForm, setTestForm] = useState({ from_phone: '+260971111234', body: 'hi' });
  const [testReply, setTestReply] = useState(null);

  const [recovery, setRecovery] = useState(null);
  const [recoveryLog, setRecoveryLog] = useState([]);
  const [showRecovery, setShowRecovery] = useState(false);
  const [recoveryRunning, setRecoveryRunning] = useState(false);

  const fetchSessions = async () => {
    setLoading(true);
    try {
      const api = (await import('../../utils/api')).default;
      const [s, st] = await Promise.all([
        api.get('/whatsapp/sessions?limit=100'),
        api.get('/whatsapp/status'),
      ]);
      setSessions(s.data.items || []);
      setStatus(st.data);
    } catch (err) {
      toast({ title: 'Failed to load sessions', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const openTranscript = async (phone) => {
    setSelected(phone);
    try {
      const api = (await import('../../utils/api')).default;
      const { data } = await api.get(`/whatsapp/sessions/${encodeURIComponent(phone)}`);
      setTranscript(data);
    } catch {
      toast({ title: 'Failed to load transcript', variant: 'destructive' });
    }
  };

  const sendTest = async () => {
    if (!testForm.from_phone || !testForm.body) {
      toast({ title: 'Phone and message required', variant: 'destructive' });
      return;
    }
    try {
      const api = (await import('../../utils/api')).default;
      const { data } = await api.post('/whatsapp/test-message', testForm);
      setTestReply(data.reply);
      fetchSessions();
      if (selected === testForm.from_phone) openTranscript(selected);
    } catch (err) {
      toast({ title: 'Test failed', description: err.response?.data?.detail || 'Failed', variant: 'destructive' });
    }
  };

  const resetSession = async (phone) => {
    if (!window.confirm(`Reset state for ${phone}? Transcript is preserved.`)) return;
    try {
      const api = (await import('../../utils/api')).default;
      await api.delete(`/whatsapp/sessions/${encodeURIComponent(phone)}`);
      toast({ title: 'Session reset' });
      fetchSessions();
      if (selected === phone) openTranscript(phone);
    } catch {
      toast({ title: 'Failed to reset', variant: 'destructive' });
    }
  };

  const fetchRecovery = async () => {
    try {
      const api = (await import('../../utils/api')).default;
      const [rs, rl] = await Promise.all([
        api.get('/whatsapp/recovery/settings'),
        api.get('/whatsapp/recovery/log?limit=20'),
      ]);
      setRecovery(rs.data);
      setRecoveryLog(rl.data.items || []);
    } catch (err) {
      setRecovery({ enabled: false, thresholdMinutes: 10, maxNudges: 1, template: null });
      setRecoveryLog([]);
    }
  };

  const updateRecovery = async (patch) => {
    try {
      const api = (await import('../../utils/api')).default;
      const { data } = await api.put('/whatsapp/recovery/settings', patch);
      setRecovery(data);
      toast({ title: 'Recovery settings updated' });
    } catch (err) {
      toast({ title: 'Failed to update', variant: 'destructive' });
    }
  };

  const runRecoveryNow = async () => {
    setRecoveryRunning(true);
    try {
      const api = (await import('../../utils/api')).default;
      const { data } = await api.post('/whatsapp/recovery/run-now');
      toast({ title: 'Recovery run', description: `Evaluated ${data.evaluated}, nudged ${data.nudged}` });
      fetchRecovery();
      fetchSessions();
    } catch (err) {
      toast({ title: 'Recovery failed', description: err.response?.data?.detail || 'Failed', variant: 'destructive' });
    } finally {
      setRecoveryRunning(false);
    }
  };

  useEffect(() => {
    fetchSessions();
    fetchRecovery();
    const interval = setInterval(fetchSessions, 15000);
    return () => clearInterval(interval);
  }, []);

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto p-4 md:p-6 space-y-6" data-testid="admin-whatsapp-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <MessageSquare className="h-6 w-6 text-emerald-600" /> WhatsApp Bookings
            </h1>
            <p className="text-sm text-gray-500">Live sessions, transcripts, and a sandbox tester for the WhatsApp booking bot.</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => { setShowRecovery(true); fetchRecovery(); }} data-testid="whatsapp-recovery-btn">
              <Clock className="h-4 w-4 mr-1" /> Recovery
              {recovery?.enabled && <span className="ml-1.5 inline-block w-2 h-2 rounded-full bg-emerald-500" />}
            </Button>
            <Button variant="outline" onClick={fetchSessions} data-testid="whatsapp-refresh-btn">
              <RefreshCw className="h-4 w-4 mr-1" /> Refresh
            </Button>
            <Button onClick={() => { setShowTest(true); setTestReply(null); }} className="bg-emerald-600 hover:bg-emerald-700" data-testid="whatsapp-test-btn">
              <Send className="h-4 w-4 mr-1" /> Test message
            </Button>
          </div>
        </div>

        {status && (
          <Card className={`p-4 ${status.twilioConfigured ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
            <div className="flex gap-2 items-start">
              <AlertCircle className={`h-5 w-5 flex-shrink-0 mt-0.5 ${status.twilioConfigured ? 'text-emerald-600' : 'text-amber-600'}`} />
              <div className="text-sm">
                <p className="font-medium">{status.twilioConfigured ? 'Twilio configured' : 'Twilio not configured'}</p>
                <p className={status.twilioConfigured ? 'text-emerald-900' : 'text-amber-900'}>
                  Sandbox sender: <code className="bg-white px-1 rounded">{status.whatsappFrom || 'not set'}</code>.{' '}
                  Webhook URL: <code className="bg-white px-1 rounded">{`${window.location.origin}/api/whatsapp/inbound`}</code>{' '}
                  — set this in Twilio Console → Messaging → WhatsApp Sandbox.
                </p>
              </div>
            </div>
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          {/* Sessions list */}
          <Card className="lg:col-span-2 p-3">
            <h2 className="font-semibold text-sm text-gray-700 px-2 py-2">Sessions ({sessions.length})</h2>
            {loading && <p className="text-gray-500 text-sm px-2">Loading…</p>}
            {!loading && sessions.length === 0 && (
              <p className="text-sm text-gray-500 px-2 py-6 text-center">No WhatsApp sessions yet.</p>
            )}
            <div className="space-y-1 max-h-[70vh] overflow-y-auto" data-testid="whatsapp-sessions-list">
              {sessions.map((s) => (
                <button
                  key={s.phone}
                  onClick={() => openTranscript(s.phone)}
                  className={`w-full text-left p-3 rounded-md border transition ${selected === s.phone ? 'bg-emerald-50 border-emerald-300' : 'bg-white border-gray-200 hover:bg-gray-50'}`}
                  data-testid={`whatsapp-session-${s.phone}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="font-mono text-sm font-semibold">{s.phone}</div>
                    <ChevronRight className="h-4 w-4 text-gray-400" />
                  </div>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${stateColor(s.state)}`}>
                      {STATE_LABELS[s.state] || s.state}
                    </span>
                    <span className="text-xs text-gray-500">{s.messageCount} msgs</span>
                    {s.lastAction && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-pink-100 text-pink-700 font-medium">
                        ✓ {s.lastAction.type}
                      </span>
                    )}
                  </div>
                  {s.lastMessage && (
                    <p className="text-xs text-gray-600 mt-1 truncate">
                      {s.lastMessage.direction === 'in' ? '→ ' : '← '}
                      {s.lastMessage.body.replace(/\*/g, '').slice(0, 80)}
                    </p>
                  )}
                </button>
              ))}
            </div>
          </Card>

          {/* Transcript */}
          <Card className="lg:col-span-3 p-4 max-h-[80vh] overflow-y-auto">
            {!selected ? (
              <div className="text-center text-gray-500 py-12">
                <MessageSquare className="h-12 w-12 mx-auto text-gray-300 mb-2" />
                <p>Select a session to view the transcript.</p>
              </div>
            ) : (
              <div data-testid="whatsapp-transcript">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h3 className="font-semibold text-lg font-mono">{selected}</h3>
                    {transcript.session && (
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${stateColor(transcript.session.state)}`}>
                        {STATE_LABELS[transcript.session.state] || transcript.session.state}
                      </span>
                    )}
                  </div>
                  <Button size="sm" variant="outline" className="text-red-600" onClick={() => resetSession(selected)} data-testid="whatsapp-reset-session-btn">
                    <Trash2 className="h-3.5 w-3.5 mr-1" /> Reset state
                  </Button>
                </div>

                <div className="space-y-2">
                  {transcript.messages.map((m, idx) => (
                    <div key={idx} className={`flex ${m.direction === 'in' ? 'justify-start' : 'justify-end'}`}>
                      <div className={`max-w-[80%] px-3 py-2 rounded-lg whitespace-pre-wrap text-sm ${
                        m.direction === 'in'
                          ? 'bg-gray-100 text-gray-900 rounded-bl-sm'
                          : 'bg-emerald-600 text-white rounded-br-sm'
                      }`}>
                        <div className={`text-[10px] font-medium mb-0.5 ${m.direction === 'in' ? 'text-gray-500' : 'text-emerald-100'}`}>
                          {m.direction === 'in' ? '👤 User' : '🤖 Bot'}
                        </div>
                        <div>{m.body}</div>
                        <div className={`text-[10px] mt-1 ${m.direction === 'in' ? 'text-gray-500' : 'text-emerald-100'}`}>
                          {new Date(m.createdAt).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  ))}
                  {transcript.messages.length === 0 && (
                    <p className="text-center text-gray-500 py-6">No messages yet.</p>
                  )}
                </div>
              </div>
            )}
          </Card>
        </div>

        {/* Recovery dialog */}
        <Dialog open={showRecovery} onOpenChange={setShowRecovery}>
          <DialogContent className="sm:max-w-lg" data-testid="whatsapp-recovery-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Clock className="h-5 w-5 text-emerald-600" /> Abandoned-booking recovery</DialogTitle>
              <DialogDescription>
                Auto-nudges users who started a WhatsApp booking but never sent the final YES. Runs every 5 min when enabled.
              </DialogDescription>
            </DialogHeader>
            {!recovery ? (
              <p className="text-sm text-gray-500">Loading…</p>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 rounded border">
                  <div>
                    <p className="font-medium text-sm">Enabled</p>
                    <p className="text-xs text-gray-500">Toggle the cron job on/off.</p>
                  </div>
                  <Switch
                    checked={!!recovery.enabled}
                    onCheckedChange={(v) => updateRecovery({ enabled: v })}
                    data-testid="whatsapp-recovery-enabled-toggle"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Threshold (minutes)</Label>
                    <Input
                      type="number"
                      min="1"
                      value={recovery.thresholdMinutes}
                      onChange={(e) => setRecovery({ ...recovery, thresholdMinutes: parseInt(e.target.value, 10) || 1 })}
                      onBlur={() => updateRecovery({ thresholdMinutes: recovery.thresholdMinutes })}
                      data-testid="whatsapp-recovery-threshold-input"
                    />
                  </div>
                  <div>
                    <Label>Max nudges per session</Label>
                    <Input
                      type="number"
                      min="1"
                      max="5"
                      value={recovery.maxNudges}
                      onChange={(e) => setRecovery({ ...recovery, maxNudges: parseInt(e.target.value, 10) || 1 })}
                      onBlur={() => updateRecovery({ maxNudges: recovery.maxNudges })}
                      data-testid="whatsapp-recovery-max-nudges-input"
                    />
                  </div>
                </div>
                <div>
                  <Label>Message template</Label>
                  <textarea
                    rows={4}
                    value={recovery.template || ''}
                    onChange={(e) => setRecovery({ ...recovery, template: e.target.value })}
                    onBlur={() => updateRecovery({ template: recovery.template || '' })}
                    placeholder="Use {flow} (ride/package/bus booking) and {summary} (auto-built)."
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    data-testid="whatsapp-recovery-template-input"
                  />
                </div>
                <Button
                  onClick={runRecoveryNow}
                  disabled={recoveryRunning}
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                  data-testid="whatsapp-recovery-run-now-btn"
                >
                  <Send className="h-4 w-4 mr-1" /> {recoveryRunning ? 'Running…' : 'Run now (force)'}
                </Button>

                {recoveryLog.length > 0 && (
                  <div>
                    <p className="text-sm font-semibold mt-3 mb-1">Recent nudges</p>
                    <div className="max-h-40 overflow-y-auto text-xs space-y-1" data-testid="whatsapp-recovery-log">
                      {recoveryLog.map((l) => (
                        <div key={l.id} className="flex items-center justify-between py-1 border-b last:border-b-0">
                          <span className="font-mono">{l.phone}</span>
                          <span className="text-gray-500">{l.flow} #{l.nudgeNumber}</span>
                          <span className={l.success ? 'text-emerald-600' : 'text-red-600'}>{l.success ? '✓' : '✗'}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Test dialog */}
        <Dialog open={showTest} onOpenChange={setShowTest}>
          <DialogContent className="sm:max-w-md" data-testid="whatsapp-test-dialog">
            <DialogHeader>
              <DialogTitle>Simulate inbound message</DialogTitle>
              <DialogDescription>Bypasses Twilio. Useful for local testing & e2e verification.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>From phone (E.164)</Label>
                <Input
                  value={testForm.from_phone}
                  onChange={(e) => setTestForm({ ...testForm, from_phone: e.target.value })}
                  placeholder="+260971111234"
                  data-testid="whatsapp-test-from-input"
                />
              </div>
              <div>
                <Label>Message body</Label>
                <Input
                  value={testForm.body}
                  onChange={(e) => setTestForm({ ...testForm, body: e.target.value })}
                  placeholder="hi"
                  data-testid="whatsapp-test-body-input"
                />
              </div>
              {testReply && (
                <div className="bg-gray-100 rounded p-3 text-sm whitespace-pre-wrap" data-testid="whatsapp-test-reply">
                  <div className="text-xs text-gray-500 mb-1">Bot reply:</div>
                  {testReply}
                </div>
              )}
              <div className="flex gap-2 pt-2">
                <Button variant="outline" className="flex-1" onClick={() => setShowTest(false)}>Close</Button>
                <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700" onClick={sendTest} data-testid="whatsapp-test-send-btn">
                  <Send className="h-4 w-4 mr-1" /> Send
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminWhatsApp;
